import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppearance } from '@/hooks/useAppearance';
import { useBackButton } from '@/hooks/useBackButton';
import { useLogtoAuth } from '@/hooks/useLogtoAuth';
import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';
import { useProfileSync } from '@/hooks/useProfileSync';
import { useEngagementEngine } from '@/hooks/useEngagementEngine';
import { usePendingInvite } from '@/hooks/usePendingInvite';
import { useCloudPref, CloudPref } from '@/hooks/useCloudPref';
import { isDevBypassActive } from '@/lib/devBypass';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setTourActive } from '@/store/core.slice';
import { setBannerCompact } from '@/store/settings.slice';
import { cn } from '@/lib/cn';
import { AdminGate } from '@/components/common/AdminGate';
import { Login } from '@/components/common/Login';
import { Onboarding, type OnboardingCompleteOpts } from '@/components/onboarding/Onboarding';
import { MainCtaProvider } from '@/contexts/MainCtaContext';
import { AppBannerProvider } from '@/contexts/AppBannerContext';
import { ActiveMatchProvider } from '@/contexts/ActiveMatchContext';
import { AppBanner } from './AppBanner';
import { EngagementNudge } from './EngagementNudge';
import { MainCta } from './MainCta';
import { TabBar } from './TabBar';
import { ActiveMatchPill } from './ActiveMatchPill';
import { Backpack } from '@/components/profile/Backpack';
import { PackTab } from '@/components/profile/PackTab';

/**
 * L'ordine delle schede in basso, che è anche l'ordine dello SPAZIO: passando
 * da News (0) a Inbox (3) ci si sposta verso destra, quindi il contenuto entra
 * da destra. Deve restare allineato a `TabBar.tsx` — se qui e lì divergono, le
 * animazioni vanno dalla parte sbagliata e l'effetto è peggiore del non averle.
 *
 * Il Profilo (ultima voce) è ESCLUSO di proposito: lì il banner si stringe già,
 * e quel movimento è l'animazione di quella transizione. Aggiungerne una
 * seconda sotto darebbe due cose in moto per lo stesso gesto.
 */
const TAB_ORDER = ['/news', '/friends', '/', '/inbox', '/profile'] as const;
const PROFILE_PATH = '/profile';

/**
 * Le schermate che si prendono tutto lo schermo, senza la chrome sopra.
 *
 * Leggere un articolo è l'unica cosa che si fa a lungo e a testa bassa: banner,
 * livello, nickname e valute lì non dicono nulla di utile e costano un quinto
 * dell'altezza a ogni schermata di testo. La barra in basso resta, perché
 * uscire dev'essere sempre possibile.
 *
 * `/news/` con la barra finale, non `startsWith('/news')`: l'ELENCO delle news
 * è una scheda come le altre e la chrome ci sta bene. Sparisce solo dentro un
 * articolo.
 */
function isImmersive(pathname: string): boolean {
  return pathname.startsWith('/news/');
}

function tabIndex(pathname: string): number {
  // Confronto esatto e non `startsWith`: `/news/abc` è il DETTAGLIO di un
  // articolo, non la scheda News — entrarci non è uno spostamento laterale.
  return TAB_ORDER.indexOf(pathname as (typeof TAB_ORDER)[number]);
}

/**
 * La barra di navigazione esiste SOLO sulle cinque schede.
 *
 * Dentro una sottopagina — un articolo, il dettaglio di un torneo — non è una
 * comodità ma un errore: quella barra serve a spostarsi fra le sezioni di primo
 * livello, e da dentro un contenuto la strada è **indietro**, non di lato. In
 * più litigava con la barra d'azione della schermata (il tasto primario), due
 * strisce impilate in fondo che si contendono lo stesso pollice — ed è da lì che
 * veniva lo stacco visibile fra le due.
 *
 * Uscire resta sempre possibile: `useBackButton` tiene il tasto indietro di
 * Telegram legato alla cronologia, e quello è il gesto giusto per un contenuto.
 */
function showsTabBar(pathname: string): boolean {
  return tabIndex(pathname) >= 0;
}

/**
 * Top-level layout: drives Logto auth, wires the BackButton, and renders the
 * active route inside a flex column with a bottom TabBar. While auth is
 * resolving we render a minimal splash; if the user is not authenticated we
 * render the <Login /> CTA instead of the route tree.
 */
export function AppShell() {
  const { isAuthenticated, isLoading } = useLogtoAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  /**
   * Scorrendo, il banner si comprime; tornando in cima si riespande.
   *
   * Due soglie diverse (24px / 8px) invece di una: con una sola, fermarsi
   * esattamente sul confine fa oscillare il banner a ogni pixel di movimento.
   * Lo scarto crea una zona morta dove nessuna delle due condizioni scatta.
   */
  const bannerCompact = useAppSelector((s) => s.settings.bannerCompact);
  const onAnyScroll = useCallback(
    (e: UIEvent<HTMLElement>) => {
      const el = e.target as HTMLElement;
      const y = el.scrollTop;

      /**
       * Non comprimere se il contenuto e' appena piu' alto dello schermo.
       *
       * Comprimendo si liberano ~150px: su un contenuto che sfora di poco,
       * quello spazio basta a farlo entrare tutto, il browser azzera lo scroll
       * perche' non c'e' piu' nulla da scorrere, l'azzeramento riespande la
       * chrome e il contenuto torna a sforare. Il risultato e' un rimbalzo a
       * ogni tentativo di scorrere — visibile per esempio nelle Statistiche di
       * Battaglie, piu' corte della lista battaglie.
       *
       * La soglia sta sopra lo spazio recuperabile: se c'e' meno di tanto da
       * scorrere, comprimere non serve comunque — la chrome non e' d'intralcio
       * su una schermata che quasi ci sta.
       */
      const scorribile = el.scrollHeight - el.clientHeight;
      if (!bannerCompact && y > 24 && scorribile > 240) dispatch(setBannerCompact(true));
      else if (bannerCompact && y < 8) dispatch(setBannerCompact(false));
    },
    [bannerCompact, dispatch],
  );

  /**
   * Il contenuto entra scivolando dal lato da cui arrivi.
   *
   * La classe si toglie, si forza un reflow e si rimette: ri-assegnare la stessa
   * animazione senza quel passaggio non la fa ripartire, e tornando due volte
   * sulla stessa scheda la seconda resterebbe ferma.
   *
   * Il contenitore NON viene rimontato (niente `key` sul percorso): rimontarlo
   * azzererebbe lo scorrimento a ogni navigazione, compreso il ritorno da un
   * dettaglio a una lista, dove si perderebbe il punto in cui si era.
   */
  const location = useLocation();
  const mainRef = useRef<HTMLElement | null>(null);
  const prevTab = useRef(tabIndex(location.pathname));
  const tabBarVisible = showsTabBar(location.pathname);

  /**
   * Lo Zaino è CHROME, non contenuto del Profilo.
   *
   * Sta qui e non dentro la rotta perché la sua linguetta pende dalla barra di
   * navigazione, che vive qui: renderla dal Profilo avrebbe richiesto di
   * ancorarla in assoluto a un'altezza che dipende dall'area di sicurezza —
   * cioè indovinare da fuori una misura che questo file conosce.
   *
   * Esiste solo sul Profilo. È la stessa consapevolezza che questo file ha già
   * di quella rotta (l'animazione d'ingresso la salta, vedi sopra).
   */
  const onProfile = location.pathname === PROFILE_PATH;
  const [packOpen, setPackOpen] = useState(false);
  const { t } = useTranslation('general');

  // Uscendo dal Profilo lo zaino si chiude. Senza, tornandoci lo si ritrova
  // aperto senza averlo aperto — uno stato senza causa visibile.
  useEffect(() => {
    if (!onProfile) setPackOpen(false);
  }, [onProfile]);

  useEffect(() => {
    const el = mainRef.current;
    const to = tabIndex(location.pathname);
    const from = prevTab.current;
    prevTab.current = to;
    if (!el) return;

    // Verso il Profilo niente: il banner che si stringe È già l'animazione di
    // quella transizione, e due movimenti per un gesto solo si disturbano.
    if (location.pathname === PROFILE_PATH) return;

    // Fra due schede c'è un verso; da o verso un dettaglio no — lì lo
    // scivolamento mentirebbe su uno spostamento laterale che non è avvenuto,
    // quindi resta il solo dissolvimento.
    const cls =
      from >= 0 && to >= 0 ? (to < from ? 'screen-in-left' : 'screen-in-right') : 'screen-in';

    el.classList.remove('screen-in-left', 'screen-in-right', 'screen-in');
    void el.offsetWidth;
    el.classList.add(cls);
  }, [location.pathname]);

  const {
    value: onboardingSeen,
    ready: onboardingReady,
    set: setOnboardingSeen,
  } = useCloudPref(CloudPref.OnboardingSeen);

  const handleOnboardingComplete = useCallback(
    (opts?: OnboardingCompleteOpts) => {
      // Flag first: the wizard must never reappear, even mid-navigation.
      setOnboardingSeen('1');
      if (opts?.startTour) dispatch(setTourActive(true));
      if (opts?.navigateTo) navigate(opts.navigateTo);
      else if (opts?.startTour) navigate('/');
    },
    [setOnboardingSeen, dispatch, navigate],
  );
  // Theme engine: applies the active palette (CSS vars + Telegram chrome) and
  // persists pref changes. Mounted here so it runs for both Login and routes.
  useAppearance();
  useBackButton();
  // QSocials presence heartbeat — 30s tick while focused, /offline on
  // blur/unload. No-op until the user is authenticated (the hook checks
  // bridgeAuthenticated internally).
  usePresenceHeartbeat();
  // QSocials profile sync — push Telegram displayName + avatar to qsocials
  // once per session so friend/follow lists render real names. No-op until
  // authenticated; the hook checks bridgeAuthenticated internally.
  useProfileSync();
  // In-app engagement: derives actionable prompts (register/check-in/play) from
  // the user's tournaments and surfaces them as a nudge + inbox. No-op until
  // authenticated (the hook checks bridgeAuthenticated internally).
  useEngagementEngine();
  // L'altra meta' del deep link: il payload messo da parte al bootstrap
  // (`main.tsx` → `stashPendingStartParam`) diventa una navigazione appena
  // l'app e' USABILE. ⚠️ «Usabile» non e' «autenticato»: un ospite nuovo passa
  // dall'onboarding, e portarlo sull'invito mentre il wizard e' a schermo
  // significherebbe consumare il payload nel momento in cui non puo' agirci.
  // E' per questo che lo stash dura piu' dell'invito — v. `lib/startParam.ts`.
  usePendingInvite(isAuthenticated && onboardingReady && onboardingSeen === '1');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text">
        <p className="text-fg-muted">Loading…</p>
      </div>
    );
  }

  // Dev escape hatch: when running locally outside of Telegram (or with
  // Logto staging redirect URI not yet whitelisted), append `?dev=1` to the
  // URL to skip the auth gate and preview unauthenticated routes. Authenticated
  // RTK Query calls will fail with 401 — that's expected.
  // Latched for the tab (see lib/devBypass): react-router navigations drop the
  // query string, so re-reading location.search here killed the bypass at the
  // first SPA navigation. Compiles to `false` in production builds.
  //
  // CriGio era arrivato alla stessa diagnosi e alla stessa cura, ma scritta qui
  // dentro: la scrittura in sessionStorage finiva nel corpo del render (effetto
  // collaterale durante il render, e senza try/catch: in navigazione privata
  // `sessionStorage` puo' lanciare). Il modulo la tiene fuori e degrada.
  const devBypass = isDevBypassActive();

  if (!isAuthenticated && !devBypass) {
    return <Login />;
  }

  // First-run flag still hydrating from CloudStorage (fresh device, empty
  // local cache): hold the splash instead of flashing Hub-then-wizard.
  if (isAuthenticated && !onboardingReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text">
        <p className="text-fg-muted">Loading…</p>
      </div>
    );
  }

  const needsOnboarding = isAuthenticated && onboardingSeen !== '1';

  return (
    <ActiveMatchProvider>
      <MainCtaProvider>
        <AppBannerProvider>
          {/* h-full, not h-[100dvh]: #root already owns the height question and
          answers it with Telegram's own `--tg-viewport-stable-height` (see
          globals.css). 100dvh measured the engine window instead — taller than
          the visible WebView — so the shell overflowed and the whole page
          scrolled, TabBar and all. Long routes (Tournaments / News) get scroll
          via <main>'s overflow-y-auto; nothing above it may scroll. */}
          {/* Nessun fondo qui di proposito: la texture dell'app sta su <body> (vedi
          globals.css) e un `bg-bg` opaco su questo contenitore la coprirebbe
          interamente. Lo shell deve essere trasparente e lasciarla passare. */}
          <div className="flex h-full flex-col overflow-hidden text-text">
            {/* AdminGate sits AFTER the auth gate above: unauthenticated users
            still see <Login /> first; only authenticated users hit the admin
            check. When VITE_ADMIN_GATE_ENABLED=false (PROD) it's a pass-through. */}
            <AdminGate>
              {needsOnboarding ? (
                <>
                  <main className="min-h-0 flex-1 overflow-y-auto">
                    <Onboarding onComplete={handleOnboardingComplete} />
                  </main>
                  <MainCta />
                </>
              ) : (
                <>
                  {/* La texture di fondo tinta di cuoio, solo sul Profilo.
                      Sta PRIMA di tutto: nello strato dei posizionati vince chi
                      viene dopo nel documento, quindi mettendolo in testa ogni
                      cosa gli passa davanti senza bisogno di z-index. */}
                  {onProfile && <div aria-hidden className="pack-tint" />}

                  {/* Constant chrome, outside the scroller: same banner, same spot,
                  every screen. Routes speak through its slots (BannerSlots.tsx)
                  instead of rendering their own.
                  Tranne dentro un articolo: vedi `isImmersive`. */}
                  {!isImmersive(location.pathname) && <AppBanner />}
                  {/* La compressione del banner e' guidata da QUI, non dalla Hub:
                  vale su ogni schermata, non solo dove esiste la griglia delle
                  sezioni. In CATTURA perche' `scroll` non risale l'albero e
                  quasi tutte le rotte portano il proprio scroller interno —
                  un handler normale non vedrebbe nulla. */}
                  <main
                    ref={mainRef}
                    className={cn(
                      // `relative`: il velo del Profilo sta nello strato dei
                      // posizionati, e senza questo lo scorrevole gli finirebbe
                      // sotto. Non sposta nulla — main non ha coordinate.
                      'relative min-h-0 flex-1 overflow-y-auto',
                      // Senza la barra sotto, l'ultima riga di contenuto
                      // finirebbe dietro la barra gesti del sistema: lo spazio
                      // che prima dava la barra lo dà ora lo scorrevole.
                      !tabBarVisible &&
                        'pb-[max(env(safe-area-inset-bottom,0px),var(--tg-viewport-safe-area-inset-bottom,0px))]',
                    )}
                    onScrollCapture={onAnyScroll}
                  >
                    <Outlet />
                  </main>
                  <MainCta insetBottom={!tabBarVisible} />
                  {/* Anche queste due passano al cuoio sul Profilo. Non è
                      rifinitura: la pastiglia della partita in corso usa
                      `bg-brand/10`, e un accento al 10% sopra il marrone
                      desatura e diventa una STRISCIA SCURA sotto l'uovo — l'ha
                      segnalata CriGio come «barra nera, sembra un misuratore».
                      Non era un difetto suo: era il teal fuori dal suo ambiente. */}
                  <div className={cn('contents', onProfile && 'pack-scope')}>
                    <EngagementNudge />
                    <ActiveMatchPill />
                  </div>

                  {/* La linguetta pende dalla barra: sta ATTACCATA sopra di lei,
                      centrata sul tasto della Hub. Il contenitore non intercetta
                      i tocchi (`pointer-events-none`) perché è largo quanto lo
                      schermo e coprirebbe l'ultima riga di contenuto; solo la
                      linguetta li riprende. */}
                  {onProfile && tabBarVisible && !packOpen && (
                    <div className="pointer-events-none relative z-10 flex justify-center">
                      <PackTab
                        open={false}
                        onToggle={() => setPackOpen(true)}
                        label={t('pack.open', { defaultValue: 'Open backpack' })}
                        className="pointer-events-auto"
                      />
                    </div>
                  )}

                  {tabBarVisible && <TabBar />}

                  {onProfile && <Backpack open={packOpen} onClose={() => setPackOpen(false)} />}
                </>
              )}
            </AdminGate>
          </div>
        </AppBannerProvider>
      </MainCtaProvider>
    </ActiveMatchProvider>
  );
}
